import { AgentInterface } from "../Agent/Agent.Interface";
import { MessageAuth } from "../Com/Com.Interfaces";
import { MemberInterface } from "../Members/Members.Interface";
import { VoiceNodeCommand } from "../Node/Node.Interface";
import { UnitInterface } from "../Unit/Unit.Interface";
import { CountryCode } from "../UnitsAndLocalization/CountryCode";
import { LanguageCode } from "../UnitsAndLocalization/Languages.Interface";
import { UnitStandard } from "../UnitsAndLocalization/Units.Interface";
import { ActionTaken } from "./Message.Interface";

export interface AuthPayloadInterface extends MessageAuth {}

export interface LanguageChangePayload {
  language: LanguageCode;
  initial?: boolean;
}

export enum ActionType {
  URL = "url",
  URLinNewTab = "url_in_new_tab",
  Plain = "plain"
}

export interface ActionInterface {
  id: string;
  type: ActionType;
  text: string;
  url?: string;
  data?: any;
}

export interface ActionTakenPayload {
  messageId: string;
  actionTaken: ActionTaken;
}

export interface ButtonsPayloadInterface {
  actions: Array<ActionInterface>;
  text?: string;
}

export interface CarouselPayloadCard {
  id: string;
  mediaURL?: string;
  title?: string;
  description?: string;
  actions: Array<ActionInterface>;
}

export interface CarouselPayload {
  cards: Array<CarouselPayloadCard>;
}
// TODO: simplify this to just one start conversation interface. Right now we have two because we used to do this in two steps

export interface CreateUserAndStartConversationPayloadInterface {
  orgId: number;
  startOnQuest?: string;
  preloadVariables?: { [key: string]: string };
  isNewUser?: boolean;
}

export enum CommandPayloadCommandType {
  NavigateTo = "navigateTo",
  LoadPastMessages = "loadPastMessages",
  ClearMessages = "clearMessages",
  Analytics = "analytics",
  Custom = "custom",
  ScrollElement = "scrollElement",
  ReplaceText = "replaceText",
  ReplaceImage = "replaceImage",
  OpenOrClose = "openOrClose",
  VoiceCommand = "VoiceCommand"
}
export interface CommandPayload {
  type: CommandPayloadCommandType;
  payload: any;
}

export const OperatorVisibleCommandTypes: CommandPayloadCommandType[] = [CommandPayloadCommandType.NavigateTo, CommandPayloadCommandType.OpenOrClose];

export interface StartConversationPayloadInterface extends CreateUserAndStartConversationPayloadInterface {
  isNewUser?: boolean;
}

export enum MediaPayloadMediaType {
  Image = "image",
  Video = "video"
}
export interface MediaPayload {
  mediaType: MediaPayloadMediaType;
  mediaURL: string;
}

export enum TakeoverEventType {
  TAKEOVER_STARTED = "Started",
  TAKEOVER_ENDED = "Ended"
}

export interface TakeoverEventPayload {
  type: TakeoverEventType;
  agent?: AgentInterface;
  member?: MemberInterface;
}

export interface TextPayloadInterface {
  text: string;
  openLinksInNewTab?: boolean;
}

export enum TypingPayloadActionType {
  Started = "Started",
  Ended = "Ended"
}

export interface TypingPayloadInterface {
  action: TypingPayloadActionType;
}

export interface MultiSelectPayload {
  actions: Array<ActionInterface>;
}

export const MilesToMeters = {
  // We are adding a bit of padding to all of this so that when a address is within x miles of a zip code, it will be included even if it is in a zip code that is slightly outside of the radius
  Miles_1: 1609.34 * 1.5,
  Miles_5: 8046.72 * 1.5,
  Miles_10: 16093.4 * 1.5,
  Miles_15: 24140.2 * 1.5,
  Miles_25: 40233.6 * 1.5,
  Miles_50: 80467.2 * 1.5,
  Miles_100: 160934 * 1.5
};

export const METRIC_DEFAULT_RADIUS_METERS = 25 * 1000;

export interface LocationPickerLocation {
  id: string;
  name?: string;
  address?: string;
  imageURL?: string;
  data?: any;
}
export interface LocationPickerPayload {
  locations: LocationPickerLocation[];
  currentZip?: string;
  canChangeZip?: boolean;
  searchRadiusInMeters?: number;
  searchedZipCodes: string[];
  locationSelected: boolean;
  distanceUnit?: UnitStandard;
}

export type SpacePickerParams = {
  showPrice: boolean;
  priceField?: string; // The field to determin the price. If not set, will use the default
  includeTax?: boolean;
  showFloor: boolean;
  actionButton: string;
  smallMaxSize: number;
  mediumMaxSize: number;
  largeMaxSize: number;
  sizeSmallDescription: string;
  sizeMediumDescription: string;
  sizeLargeDescription: string;
  sizeExtraLargeDescription: string;
  sizeParkingDescription: string;
  preSelectedDimensions: string[];
  preSelectedFeatures: string[];
  featureDisplayName?: { [featureName: string]: string };
  onlyShowUnitDimensions?: string[];
  currencySymbol?: string;
};
export interface SpacePickerPayload {
  spaces: UnitInterface[];
  params: SpacePickerParams;
  location?: LocationPickerLocation;
  selectedSpace?: UnitInterface;
}

export enum ProfileCollectStatus {
  PENDING = "PENDING",
  SAVED = "SAVED",
  CANCELED = "CANCELED"
}
export interface ProfileCollectPayload {
  items: ProfileCollectDataItem[];
  actionButtonText: string;
  status: ProfileCollectStatus;
  countryCodes?: CountryCode[]; // Used to validate
}
export interface ProfileCollectDataItem {
  id: string;
  label: string;
  required: boolean;
  placeholder?: string;
  value?: string;
}

// Interfaces for text, image, and template payloads
export interface TextMessagePayload {
  text: string;
}

export interface ImageMessagePayload {
  imageUrl: string;
}

export interface TemplateMessagePayload {
  template: any; // Will be more specific once application requirements are set
}

export interface UserShouldMergePayload {
  mergeFromAuth: { userId: number; token: string };
  mergeToAuth: { userId: number; token: string };
}

export type ValidMessagePayload =
  | AuthPayloadInterface
  | ActionTakenPayload
  | ButtonsPayloadInterface
  | CarouselPayload
  | CreateUserAndStartConversationPayloadInterface
  | CommandPayload
  | MediaPayload
  | MultiSelectPayload
  | StartConversationPayloadInterface
  | TakeoverEventPayload
  | TextPayloadInterface
  | TypingPayloadInterface
  | LocationPickerPayload
  | SpacePickerPayload
  | ProfileCollectPayload
  | VoiceNodeCommand
  | TextMessagePayload
  | ImageMessagePayload
  | TemplateMessagePayload
  | UserShouldMergePayload
  | LanguageChangePayload;
